import XLSX from 'xlsx-js-style';

// ============================================================
// 타입 정의
// ============================================================

export type ProjectRoundSaleRow = {
  amt: number;
  issueDay: string | number;
  issueYn: boolean;
  paymentDay: string | number;
  paymentYn: boolean;
  memo: string | null;
};

export type PurchaseAssignmentRow = {
  category: string;
  plan: string;
  contractAmt: number;
  usedAmt: number;
  memo: string | null;
};

export type PurchaseOtherRow = {
  category: string;
  vender: string;
  purchaseAmt: number;
  issueDay: string | number | null;
  issueYn: boolean;
  paymentDay: string | number;
  paymentYn: boolean;
  memo: string | null;
};

// ============================================================
// 컬럼 설정
// ============================================================

const PROJECT_ROUND_SALES_COLUMNS = [
  { key: 'amt', header: '기성금액', width: 15, type: 'number' as const },
  { key: 'issueDay', header: '세금계산서 발행일', width: 18, type: 'date' as const },
  { key: 'issueYn', header: '발행여부', width: 10, type: 'boolean' as const },
  { key: 'paymentDay', header: '수금일', width: 18, type: 'date' as const },
  { key: 'paymentYn', header: '수금여부', width: 10, type: 'boolean' as const },
  { key: 'memo', header: '비고', width: 30, type: 'string' as const },
];

const PURCHASE_ASSIGNMENT_COLUMNS = [
  { key: 'category', header: '비목', width: 20, type: 'string' as const },
  { key: 'plan', header: '계획', width: 25, type: 'string' as const },
  { key: 'contractAmt', header: '협약금액', width: 15, type: 'number' as const },
  { key: 'usedAmt', header: '사용금액', width: 15, type: 'number' as const },
  { key: 'remainingAmt', header: '잔액', width: 15, type: 'number' as const },
  { key: 'memo', header: '비고', width: 30, type: 'string' as const },
];

// 업로드 시 잔액 컬럼 제외 (자동 계산 필드)
const PURCHASE_ASSIGNMENT_UPLOAD_COLUMNS = [
  { key: 'category', header: '비목', width: 20, type: 'string' as const },
  { key: 'plan', header: '계획', width: 25, type: 'string' as const },
  { key: 'contractAmt', header: '협약금액', width: 15, type: 'number' as const },
  { key: 'usedAmt', header: '사용금액', width: 15, type: 'number' as const },
  { key: 'memo', header: '비고', width: 30, type: 'string' as const },
];

const PURCHASE_OTHER_COLUMNS = [
  { key: 'category', header: '항목', width: 20, type: 'string' as const },
  { key: 'vender', header: '업체', width: 20, type: 'string' as const },
  { key: 'purchaseAmt', header: '매입금액', width: 15, type: 'number' as const },
  { key: 'issueDay', header: '세금계산서 발행일', width: 18, type: 'date' as const },
  { key: 'issueYn', header: '발행여부', width: 10, type: 'boolean' as const },
  { key: 'paymentDay', header: '대금 지급일', width: 18, type: 'date' as const },
  { key: 'paymentYn', header: '지급여부', width: 10, type: 'boolean' as const },
  { key: 'memo', header: '비고', width: 30, type: 'string' as const },
];

// ============================================================
// 공통 유틸리티 함수
// ============================================================

/**
 * 헤더 스타일 정의
 */
const getHeaderStyle = () => ({
  font: { bold: true, color: { rgb: 'FFFFFF' } },
  fill: { fgColor: { rgb: '4472C4' } },
  alignment: { horizontal: 'center' as const, vertical: 'center' as const },
  border: {
    top: { style: 'thin' as const, color: { rgb: '000000' } },
    bottom: { style: 'thin' as const, color: { rgb: '000000' } },
    left: { style: 'thin' as const, color: { rgb: '000000' } },
    right: { style: 'thin' as const, color: { rgb: '000000' } },
  },
});

/**
 * 데이터 셀 스타일 정의
 */
const getDataStyle = (options?: { wrapText?: boolean; isDate?: boolean }) => ({
  alignment: { vertical: 'center' as const, wrapText: options?.wrapText },
  border: {
    top: { style: 'thin' as const, color: { rgb: 'CCCCCC' } },
    bottom: { style: 'thin' as const, color: { rgb: 'CCCCCC' } },
    left: { style: 'thin' as const, color: { rgb: 'CCCCCC' } },
    right: { style: 'thin' as const, color: { rgb: 'CCCCCC' } },
  },
  ...(options?.isDate && { numFmt: 'YYYY-MM-DD' }),
});

/**
 * 병합 셀 스타일 정의
 */
const getMergedCellStyle = () => ({
  alignment: { vertical: 'center' as const, horizontal: 'center' as const },
  border: {
    top: { style: 'thin' as const, color: { rgb: 'CCCCCC' } },
    bottom: { style: 'thin' as const, color: { rgb: 'CCCCCC' } },
    left: { style: 'thin' as const, color: { rgb: 'CCCCCC' } },
    right: { style: 'thin' as const, color: { rgb: 'CCCCCC' } },
  },
});

/**
 * 날짜 값을 YYYY-MM-DD 문자열로 변환
 */
const formatDateValue = (value: string | number | undefined): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

/**
 * 엑셀 날짜 시리얼 값을 타임스탬프로 변환
 * UTC 기반 계산으로 시간대 문제 방지
 */
const excelSerialToTimestamp = (serial: number): number => {
  // 엑셀 기준일: 1899-12-30 (1900-01-01이 serial 1)
  // UTC로 계산하여 시간대 영향 제거
  const excelEpochUTC = Date.UTC(1899, 11, 30);
  const dateUTC = excelEpochUTC + serial * 24 * 60 * 60 * 1000;
  const date = new Date(dateUTC);
  // UTC 날짜를 로컬 정오 시간으로 변환
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 12, 0, 0).getTime();
};

/**
 * YYYY-MM-DD 문자열을 타임스탬프로 변환
 */
const dateStringToTimestamp = (dateStr: string): number => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day, 12, 0, 0).getTime();
};

/**
 * 셀 값을 파싱하여 적절한 타입으로 변환
 * date 타입의 경우 timestamp(number)를 반환
 */
const parseCellValue = (
  value: unknown,
  type: 'string' | 'number' | 'boolean' | 'date',
): string | number | boolean => {
  if (value === undefined || value === null || value === '') {
    switch (type) {
      case 'number':
        return 0;
      case 'boolean':
        return false;
      case 'date':
        return 0; // 빈 값은 0 반환, 호출부에서 적절히 처리
      default:
        return '';
    }
  }

  switch (type) {
    case 'number': {
      const num = typeof value === 'string' ? Number(value.replace(/,/g, '')) : Number(value);
      return isNaN(num) ? 0 : num;
    }
    case 'boolean': {
      if (typeof value === 'boolean') return value;
      const strVal = String(value).toLowerCase().trim();
      return (
        strVal === 'y' || strVal === 'yes' || strVal === '예' || strVal === 'true' || strVal === '1'
      );
    }
    case 'date': {
      // 날짜를 timestamp로 변환하여 반환
      if (typeof value === 'number') {
        // 엑셀 날짜 시리얼 값을 타임스탬프로 변환
        return excelSerialToTimestamp(value);
      }
      if (typeof value === 'string') {
        // YYYY-MM-DD 형식
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (dateRegex.test(value)) {
          return dateStringToTimestamp(value);
        }
        // 다른 형식 시도
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0).getTime();
        }
      }
      // Date 객체인 경우
      if (value instanceof Date && !isNaN(value.getTime())) {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 12, 0, 0).getTime();
      }
      return 0;
    }
    default:
      return String(value);
  }
};

/**
 * 워크시트 생성 공통 함수
 */
const createWorksheet = <T extends Record<string, unknown>>(
  data: T[],
  columns: {
    key: string;
    header: string;
    width: number;
    type: 'string' | 'number' | 'boolean' | 'date';
  }[],
  options?: { wrapTextColumns?: string[] },
): XLSX.WorkSheet => {
  // 헤더 행 생성
  const headers = columns.map((col) => col.header);
  const wsData: unknown[][] = [headers];

  // 데이터 행 생성
  data.forEach((row) => {
    const rowData = columns.map((col) => {
      const value = row[col.key];
      if (col.type === 'date') {
        return formatDateValue(value as string | number | undefined);
      }
      if (col.type === 'boolean') {
        return value ? 'Y' : 'N';
      }
      return value ?? '';
    });
    wsData.push(rowData);
  });

  // 데이터가 없으면 예시 행 추가
  if (data.length === 0) {
    const exampleRow = columns.map((col) => {
      switch (col.type) {
        case 'number':
          return '';
        case 'boolean':
          return 'Y 또는 N';
        case 'date':
          return 'YYYY-MM-DD';
        default:
          return '';
      }
    });
    wsData.push(exampleRow);
  }

  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // 컬럼 너비 설정
  ws['!cols'] = columns.map((col) => ({ wch: col.width }));

  // 스타일 적용
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');

  // 헤더 스타일
  for (let C = range.s.c; C <= range.e.c; C++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
    if (ws[cellAddress]) {
      ws[cellAddress].s = getHeaderStyle();
    }
  }

  // 데이터 스타일
  const wrapTextColIndices = (options?.wrapTextColumns || []).map((key) =>
    columns.findIndex((col) => col.key === key),
  );
  const dateColIndices = columns
    .map((col, idx) => (col.type === 'date' ? idx : -1))
    .filter((idx) => idx !== -1);

  for (let R = 1; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      if (ws[cellAddress]) {
        const isWrapText = wrapTextColIndices.includes(C);
        const isDate = dateColIndices.includes(C);
        ws[cellAddress].s = getDataStyle({ wrapText: isWrapText, isDate });
      }
    }
  }

  return ws;
};

/**
 * 파일 다운로드 공통 함수
 */
const downloadWorkbook = (ws: XLSX.WorkSheet, fileName: string, sheetName: string): void => {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, fileName);
};

/**
 * 파일 업로드 파싱 공통 함수
 */
const parseExcelFile = async <T>(
  file: File,
  columns: {
    key: string;
    header: string;
    width: number;
    type: 'string' | 'number' | 'boolean' | 'date';
  }[],
  validator?: (row: T, index: number) => string | null,
): Promise<{ data: T[]; errors: string[] }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        // cellDates: false로 raw 시리얼 값을 받아서 직접 변환 (시간대 문제 방지)
        const workbook = XLSX.read(data, { type: 'array', cellDates: false });

        // 첫 번째 시트 사용
        const sheetName = workbook.SheetNames[0];
        if (!sheetName) {
          reject(new Error('엑셀 파일에 시트가 없습니다.'));
          return;
        }

        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as unknown[][];

        if (jsonData.length < 2) {
          resolve({ data: [], errors: ['데이터가 없습니다. 헤더 아래에 데이터를 입력해주세요.'] });
          return;
        }

        // 헤더 검증
        const headerRow = jsonData[0] as string[];
        const expectedHeaders = columns.map((col) => col.header);
        const headerMismatch = expectedHeaders.some((header, idx) => {
          const actualHeader = headerRow[idx]?.toString().trim();
          return actualHeader !== header;
        });

        if (headerMismatch) {
          resolve({
            data: [],
            errors: [`헤더가 일치하지 않습니다. 예상: [${expectedHeaders.join(', ')}]`],
          });
          return;
        }

        const parsedData: T[] = [];
        const errors: string[] = [];

        // 데이터 행 파싱 (헤더 제외)
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i] as unknown[];

          // 빈 행 건너뛰기
          if (!row || row.every((cell) => cell === undefined || cell === null || cell === '')) {
            continue;
          }

          const parsedRow: Record<string, unknown> = {};

          columns.forEach((col, colIdx) => {
            const cellValue = row[colIdx];
            parsedRow[col.key] = parseCellValue(cellValue, col.type);
          });

          // 유효성 검사
          if (validator) {
            const error = validator(parsedRow as T, i);
            if (error) {
              errors.push(`${i + 1}행: ${error}`);
            }
          }

          parsedData.push(parsedRow as T);
        }

        resolve({ data: parsedData, errors });
      } catch (error) {
        reject(new Error('엑셀 파일 파싱 중 오류가 발생했습니다.'));
      }
    };

    reader.onerror = () => {
      reject(new Error('파일 읽기 중 오류가 발생했습니다.'));
    };

    reader.readAsArrayBuffer(file);
  });
};

// ============================================================
// 기성내역 (ProjectRoundSales)
// ============================================================

export const downloadProjectRoundSalesTemplate = (data: ProjectRoundSaleRow[]): void => {
  const ws = createWorksheet(data, PROJECT_ROUND_SALES_COLUMNS, { wrapTextColumns: ['memo'] });
  downloadWorkbook(ws, '기성내역.xlsx', '기성내역');
};

export const parseProjectRoundSalesExcel = async (
  file: File,
): Promise<{ data: ProjectRoundSaleRow[]; errors: string[] }> => {
  const validator = (row: ProjectRoundSaleRow, index: number): string | null => {
    if (row.amt <= 0) {
      return `기성금액은 1 이상이어야 합니다. (현재: ${row.amt})`;
    }
    return null;
  };

  const result = await parseExcelFile<ProjectRoundSaleRow>(
    file,
    PROJECT_ROUND_SALES_COLUMNS,
    validator,
  );

  // 날짜 필드 기본값 처리
  result.data = result.data.map((row) => ({
    ...row,
    issueDay: row.issueDay || Date.now(),
    paymentDay: row.paymentDay || Date.now(),
    memo: row.memo || null,
  }));

  return result;
};

// ============================================================
// 과제용 매입내역 (PurchaseAssignment)
// ============================================================

/**
 * 과제용 매입내역 다운로드 (비목 병합, 비고 줄바꿈 지원)
 */
export const downloadPurchaseAssignmentTemplate = (data: PurchaseAssignmentRow[]): void => {
  const columns = PURCHASE_ASSIGNMENT_COLUMNS;

  // 헤더 행 생성
  const headers = columns.map((col) => col.header);
  const wsData: unknown[][] = [headers];

  // 비목별로 정렬 (같은 비목끼리 모이도록)
  const sortedData = [...data].sort((a, b) => a.category.localeCompare(b.category));

  // 데이터 행 생성 (잔액 계산 포함)
  sortedData.forEach((row) => {
    const rowData = columns.map((col) => {
      if (col.key === 'remainingAmt') {
        // 잔액 = 협약금액 - 사용금액
        return (row.contractAmt || 0) - (row.usedAmt || 0);
      }
      const value = row[col.key as keyof PurchaseAssignmentRow];
      return value ?? '';
    });
    wsData.push(rowData);
  });

  // 데이터가 없으면 예시 행 추가
  if (data.length === 0) {
    const exampleRow = columns.map((col) => {
      switch (col.type) {
        case 'number':
          return '';
        default:
          return '';
      }
    });
    wsData.push(exampleRow);
  }

  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // 컬럼 너비 설정
  ws['!cols'] = columns.map((col) => ({ wch: col.width }));

  // 스타일 적용
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');

  // 헤더 스타일
  for (let C = range.s.c; C <= range.e.c; C++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
    if (ws[cellAddress]) {
      ws[cellAddress].s = getHeaderStyle();
    }
  }

  // 비목 컬럼 인덱스 (0번)
  const categoryColIdx = 0;
  // 비고 컬럼 인덱스 (4번)
  const memoColIdx = columns.findIndex((col) => col.key === 'memo');

  // 데이터 스타일 및 비목 병합 계산
  const merges: XLSX.Range[] = [];
  let mergeStartRow = 1;
  let currentCategory = sortedData[0]?.category;

  for (let R = 1; R <= range.e.r; R++) {
    const dataIdx = R - 1;
    const rowCategory = sortedData[dataIdx]?.category;

    // 비목이 변경되었거나 마지막 행인 경우 병합 처리
    if (rowCategory !== currentCategory || R === range.e.r) {
      const mergeEndRow = rowCategory !== currentCategory ? R - 1 : R;

      // 2개 이상 행이 같은 비목인 경우에만 병합
      if (mergeEndRow > mergeStartRow) {
        merges.push({
          s: { r: mergeStartRow, c: categoryColIdx },
          e: { r: mergeEndRow, c: categoryColIdx },
        });
      }

      if (rowCategory !== currentCategory) {
        mergeStartRow = R;
        currentCategory = rowCategory;
      }
    }

    // 각 셀에 스타일 적용
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      if (ws[cellAddress]) {
        if (C === categoryColIdx) {
          // 비목 컬럼: 병합 셀 스타일
          ws[cellAddress].s = getMergedCellStyle();
        } else if (C === memoColIdx) {
          // 비고 컬럼: 줄바꿈 활성화
          ws[cellAddress].s = getDataStyle({ wrapText: true });
        } else {
          ws[cellAddress].s = getDataStyle();
        }
      }
    }
  }

  // 병합 설정
  if (merges.length > 0) {
    ws['!merges'] = merges;
  }

  downloadWorkbook(ws, '매입내역_과제.xlsx', '매입내역');
};

/**
 * 과제용 매입내역 엑셀 파싱 (병합된 비목 셀 처리)
 */
export const parsePurchaseAssignmentExcel = async (
  file: File,
): Promise<{ data: PurchaseAssignmentRow[]; errors: string[] }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        // cellDates: false로 raw 시리얼 값을 받아서 직접 변환 (시간대 문제 방지)
        const workbook = XLSX.read(data, { type: 'array', cellDates: false });

        const sheetName = workbook.SheetNames[0];
        if (!sheetName) {
          reject(new Error('엑셀 파일에 시트가 없습니다.'));
          return;
        }

        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as unknown[][];

        if (jsonData.length < 2) {
          resolve({ data: [], errors: ['데이터가 없습니다. 헤더 아래에 데이터를 입력해주세요.'] });
          return;
        }

        // 헤더 검증 (잔액 컬럼 포함/미포함 모두 허용)
        const headerRow = jsonData[0] as string[];
        const expectedHeadersWithRemaining = PURCHASE_ASSIGNMENT_COLUMNS.map((col) => col.header);
        const expectedHeadersWithoutRemaining = PURCHASE_ASSIGNMENT_UPLOAD_COLUMNS.map(
          (col) => col.header,
        );

        // 잔액 포함 헤더 검증
        const hasRemainingColumn = headerRow.length >= expectedHeadersWithRemaining.length;
        const headerMismatchWithRemaining = hasRemainingColumn
          ? expectedHeadersWithRemaining.some((header, idx) => {
              const actualHeader = headerRow[idx]?.toString().trim();
              return actualHeader !== header;
            })
          : true;

        // 잔액 미포함 헤더 검증
        const headerMismatchWithoutRemaining = expectedHeadersWithoutRemaining.some(
          (header, idx) => {
            const actualHeader = headerRow[idx]?.toString().trim();
            return actualHeader !== header;
          },
        );

        if (headerMismatchWithRemaining && headerMismatchWithoutRemaining) {
          resolve({
            data: [],
            errors: [
              `헤더가 일치하지 않습니다. 예상: [${expectedHeadersWithoutRemaining.join(', ')}]`,
            ],
          });
          return;
        }

        // 잔액 컬럼 포함 여부 확인
        const includesRemainingColumn = !headerMismatchWithRemaining;

        const parsedData: PurchaseAssignmentRow[] = [];
        const errors: string[] = [];
        let lastCategory = ''; // 병합된 셀 처리를 위한 이전 비목 값

        // 데이터 행 파싱 (헤더 제외)
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i] as unknown[];

          // 빈 행 건너뛰기
          if (!row || row.every((cell) => cell === undefined || cell === null || cell === '')) {
            continue;
          }

          // 비목 값 처리 (병합된 셀은 첫 행만 값이 있음)
          const categoryValue = row[0];
          const category =
            categoryValue !== undefined && categoryValue !== null && categoryValue !== ''
              ? String(categoryValue).trim()
              : lastCategory; // 빈 값이면 이전 비목 사용

          lastCategory = category;

          // 비고 컬럼 인덱스: 잔액 컬럼 포함 시 5, 미포함 시 4
          const memoColIdx = includesRemainingColumn ? 5 : 4;

          const parsedRow: PurchaseAssignmentRow = {
            category,
            plan: String(row[1] ?? '').trim(),
            contractAmt: parseCellValue(row[2], 'number') as number,
            usedAmt: parseCellValue(row[3], 'number') as number,
            memo:
              row[memoColIdx] !== undefined && row[memoColIdx] !== null
                ? String(row[memoColIdx])
                : null,
          };

          // 유효성 검사
          const rowErrors: string[] = [];

          if (!parsedRow.category) {
            rowErrors.push('비목은 필수입니다.');
          } else if (parsedRow.category.length > 100) {
            rowErrors.push('비목은 100자 이내여야 합니다.');
          }

          if (!parsedRow.plan) {
            rowErrors.push('계획은 필수입니다.');
          } else if (parsedRow.plan.length > 100) {
            rowErrors.push('계획은 100자 이내여야 합니다.');
          }

          if (parsedRow.contractAmt < 0) {
            rowErrors.push('협약금액은 0 이상이어야 합니다.');
          }

          if (parsedRow.usedAmt < 0) {
            rowErrors.push('사용금액은 0 이상이어야 합니다.');
          }

          if (rowErrors.length > 0) {
            errors.push(`${i + 1}행: ${rowErrors.join(', ')}`);
          }

          parsedData.push(parsedRow);
        }

        resolve({ data: parsedData, errors });
      } catch (error) {
        reject(new Error('엑셀 파일 파싱 중 오류가 발생했습니다.'));
      }
    };

    reader.onerror = () => {
      reject(new Error('파일 읽기 중 오류가 발생했습니다.'));
    };

    reader.readAsArrayBuffer(file);
  });
};

// ============================================================
// 그 외 매입내역 (PurchaseOther)
// ============================================================

export const downloadPurchaseOtherTemplate = (data: PurchaseOtherRow[]): void => {
  const ws = createWorksheet(data, PURCHASE_OTHER_COLUMNS, { wrapTextColumns: ['memo'] });
  downloadWorkbook(ws, '매입내역.xlsx', '매입내역');
};

export const parsePurchaseOtherExcel = async (
  file: File,
): Promise<{ data: PurchaseOtherRow[]; errors: string[] }> => {
  const validator = (row: PurchaseOtherRow, index: number): string | null => {
    const errors: string[] = [];

    if (!row.category || row.category.trim() === '') {
      errors.push('항목은 필수입니다.');
    } else if (row.category.length > 100) {
      errors.push('항목은 100자 이내여야 합니다.');
    }

    if (!row.vender || row.vender.trim() === '') {
      errors.push('업체는 필수입니다.');
    } else if (row.vender.length > 100) {
      errors.push('업체는 100자 이내여야 합니다.');
    }

    return errors.length > 0 ? errors.join(', ') : null;
  };

  const result = await parseExcelFile<PurchaseOtherRow>(file, PURCHASE_OTHER_COLUMNS, validator);

  // 날짜 필드 처리 (issueDay는 null 허용, paymentDay는 기본값 현재 timestamp)
  result.data = result.data.map((row) => ({
    ...row,
    category: row.category?.trim() || '',
    vender: row.vender?.trim() || '',
    issueDay: row.issueDay || null, // 0이면 null 처리
    paymentDay: row.paymentDay || Date.now(), // 0이면 현재 timestamp
    memo: row.memo || null,
  }));

  return result;
};
